/**
 * A backend error carrying the typed `code` the API returns (e.g.
 * `system_role_immutable`, `role_in_use`). Shared across every module so that
 * consumers branch on `instanceof ApiError && error.code` instead of a
 * per-module error class (ADR 0012).
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string | null;
  /** Optional structured context the backend attaches to some errors (e.g. the other
   *  Venue on a cross-venue shift conflict). Null when the body carried none. */
  readonly details: Record<string, string | null> | null;
  constructor(
    status: number,
    code: string | null,
    message: string,
    details: Record<string, string | null> | null = null,
  ) {
    super(message);
    this.status = status;
    this.code = code;
    this.details = details;
    this.name = 'ApiError';
  }
}

/** The non-throwing result shape openapi-fetch resolves to. */
interface FetchResult<T> {
  data?: T;
  error?: { code?: string; message?: string; details?: Record<string, string | null> };
  response: Response;
}

/**
 * Adapt openapi-fetch's `{ data, error, response }` result into the throw-or-return
 * contract the modules expect: resolve to typed `data` on success (with `204`
 * mapping to `undefined`), or throw {@link ApiError} carrying the backend's
 * `(status, code, message)` on failure.
 */
export async function unwrap<T>(call: Promise<FetchResult<T>>): Promise<T> {
  const { data, error, response } = await call;
  if (response.ok) {
    if (response.status === 204) return undefined as T;
    return data as T;
  }
  throw new ApiError(
    response.status,
    error?.code ?? null,
    error?.message ?? `Request failed (${response.status})`,
    error?.details ?? null,
  );
}
